import { useEffect, useRef } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Loader } from "shared/components/Loader";
import { Show } from "shared/components";
import { HighlightText } from "shared/components/HighlightText";
import { cx } from "shared/utils";
import type {
  MasterSearchEntityType,
  MasterSearchGroup,
  MasterSearchItem
} from "api/masterSearch";
import { ENTITY_TYPE_ICONS } from "./constants";
import type { MasterSearchQuickLink } from "./routes";
import { orderVisibleGroups } from "./utils";

interface MasterSearchResultsProps {
  query: string;
  minQueryLength: number;
  groups?: MasterSearchGroup[];
  isFetching: boolean;
  isError: boolean;
  activeIndex: number;
  allowedTypes: MasterSearchEntityType[];
  typeLabels: Record<MasterSearchEntityType, string>;
  quickLinksFor: (item: MasterSearchItem) => MasterSearchQuickLink[];
  onSelect: (item: MasterSearchItem) => void;
  onQuickLink: (route: string) => void;
  onHover: (index: number) => void;
}

const QuickLinkChips = ({
  links,
  onQuickLink
}: {
  links: MasterSearchQuickLink[];
  onQuickLink: (route: string) => void;
}) => (
  <span className="flex shrink-0 items-center gap-1.5">
    {links.map((link) => (
      <button
        key={link.key}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onQuickLink(link.route);
        }}
        className="rounded-full border border-alpha-100 bg-white px-2.5 py-1 text-xs text-content-500 transition-all hover:border-primary-500 hover:text-primary-500"
      >
        {link.label}
      </button>
    ))}
  </span>
);

const EmptyState = ({
  allowedTypes,
  typeLabels
}: {
  allowedTypes: MasterSearchEntityType[];
  typeLabels: Record<MasterSearchEntityType, string>;
}) => (
  <div className="px-6 py-8 text-center">
    <p className="text-sm text-content-500">
      Search everyone across your products
    </p>
    <p className="mt-1 text-xs text-content-200">
      by name, email, phone number, or address
    </p>
    <div className="mt-5 flex flex-wrap justify-center gap-2">
      {allowedTypes.map((entityType) => (
        <span
          key={entityType}
          className="flex items-center gap-1.5 rounded-full border border-alpha-100 bg-alpha-20 px-2.5 py-1 text-xs text-content-500"
        >
          <HugeiconsIcon
            icon={ENTITY_TYPE_ICONS[entityType]}
            size={13}
            className="stroke-content-200"
            aria-hidden
          />
          {typeLabels[entityType]}
        </span>
      ))}
    </div>
  </div>
);

export const MasterSearchResults = ({
  query,
  minQueryLength,
  groups,
  isFetching,
  isError,
  activeIndex,
  allowedTypes,
  typeLabels,
  quickLinksFor,
  onSelect,
  onQuickLink,
  onHover
}: MasterSearchResultsProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const visibleGroups = orderVisibleGroups(groups ?? []);

  // Keep the keyboard-active row visible — also when a new result set paints
  // into a container that kept its previous scroll offset.
  useEffect(() => {
    listRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, groups]);

  if (query.length < minQueryLength)
    return <EmptyState allowedTypes={allowedTypes} typeLabels={typeLabels} />;

  if (isError)
    return (
      <p className="p-4 text-sm text-danger-500">
        Couldn&apos;t load search results. Please try again.
      </p>
    );

  if (isFetching && visibleGroups.length === 0)
    return (
      <div className="flex items-center justify-center p-8">
        <Loader size={24} />
      </div>
    );

  if (visibleGroups.length === 0)
    return (
      <p className="p-6 text-center text-sm text-content-200">
        No results for &ldquo;{query}&rdquo;.
      </p>
    );

  let runningIndex = -1;

  return (
    <div
      ref={listRef}
      role="listbox"
      aria-label="Search results"
      className={cx(
        "max-h-[min(525px,65vh)] overflow-y-auto py-2 transition-opacity",
        isFetching ? "opacity-60" : "opacity-100"
      )}
    >
      {visibleGroups.map((group) => (
        <div
          key={group.type}
          role="group"
          aria-label={typeLabels[group.type]}
          className="py-1"
        >
          <div className="flex items-center gap-2 px-4 py-1.5">
            <HugeiconsIcon
              icon={ENTITY_TYPE_ICONS[group.type]}
              size={15}
              className="shrink-0 stroke-content-200"
              aria-hidden
            />
            <span className="text-xs font-medium uppercase tracking-wide text-content-200">
              {typeLabels[group.type]}
            </span>
            <span className="rounded-full bg-alpha-40 px-1.5 text-[10px] font-medium text-content-200">
              {group.total > group.items.length
                ? `${group.items.length} of ${group.total}`
                : group.total}
            </span>
          </div>
          {group.items.map((item) => {
            runningIndex += 1;
            const itemIndex = runningIndex;
            const active = itemIndex === activeIndex;
            const quickLinks = quickLinksFor(item);
            return (
              // Not a <button>: quick links render as real buttons inside the
              // row, and interactive elements must not nest. Keyboard access
              // stays on the search input (arrows + Enter), so the row itself
              // only needs the option semantics.
              <div
                key={`${item.type}-${item.id}`}
                role="option"
                aria-selected={active}
                data-active={active}
                onClick={() => onSelect(item)}
                onMouseEnter={() => onHover(itemIndex)}
                className={cx(
                  "relative flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left transition-all",
                  active ? "bg-alpha-40" : ""
                )}
              >
                <Show if={active}>
                  <span className="absolute inset-y-1 left-0 w-0.5 rounded-r bg-primary-500" />
                </Show>
                <span className="min-w-0 grow">
                  <span className="flex items-center gap-1.5">
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-content-1000">
                      <HighlightText text={item.title} query={query} />
                    </span>
                    <Show if={item.context?.listed === false}>
                      <span className="shrink-0 rounded-full border border-alpha-100 bg-alpha-20 px-1.5 text-[10px] font-medium text-content-300">
                        Not listed
                      </span>
                    </Show>
                  </span>
                  <Show if={item.subtitle}>
                    <span className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs text-content-200">
                      <HighlightText text={item.subtitle ?? ""} query={query} />
                    </span>
                  </Show>
                </span>
                <Show if={quickLinks.length > 0}>
                  <QuickLinkChips
                    links={quickLinks}
                    onQuickLink={onQuickLink}
                  />
                </Show>
                {/* Always occupies its slot so chips don't shift when the
                    active row changes. */}
                <kbd
                  className={cx(
                    "shrink-0 rounded border border-alpha-100 bg-white px-1 font-sans text-[10px] text-content-200",
                    active ? "opacity-100" : "opacity-0"
                  )}
                  aria-hidden={!active}
                >
                  ↵
                </kbd>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
